/**
 * platform/jobsHealth.ts — how the platform console reads
 * `GET /api/system/jobs/health`.
 *
 * ===========================================================================
 * WHY THIS FILE EXISTS (PH-ATT-005)
 * ===========================================================================
 *
 * The API side of durable-job health is done and was done carefully.
 * `jobs/queueHealth.ts` sorts every queue into a closed disposition and scores
 * it; `domains/system/jobsHealth.ts` serves that per queue behind
 * `platform:tenancy.administer` and WITHHOLDS counts rather than zeroing them
 * when the probe cannot run; `jobs/queueHealthReporter.ts` pushes an ERROR line
 * per unhealthy queue every five minutes; `/ready` degrades on a dead executor.
 *
 * And then the only human-readable rendering of all of it parsed the body as
 * `{ status?: string }` and printed that one word. Which queue, which
 * disposition, whether the probe ran at all — discarded at the last hop, by the
 * client, after the server had gone to considerable trouble to distinguish
 * them. An operator reading "error" learned that something was wrong and
 * nothing else; an operator reading "degraded" could not tell a bounded deploy
 * pause from a probe that failed to read.
 *
 * So the reading is done here, as a pure function over the body, and the screen
 * renders the result. Pure because a reading that can only be exercised by
 * mounting a component is a reading whose refusals never get tested — and the
 * refusals are the whole point.
 *
 * ===========================================================================
 * THE CLOSED SET, AND WHY EACH MEMBER IS SEPARATE
 * ===========================================================================
 *
 *   unhealthy  something is wrong AND we can say what: which queues, which
 *              dispositions, or which executor state.
 *   paused     claiming is deliberately stopped for a bounded deploy handoff.
 *              Real, worth a look, NOT an incident — the disposition exists
 *              precisely so this is never read as `blocked` or `stopped`.
 *   unknown    the queues answered, but with a verdict that is an admission:
 *              `unknown` (the probe could not read a queue) or `runtime_gap`
 *              (a silence that predates any runtime able to vouch for it).
 *   withheld   the durable probe did not run at all, so there are no counts.
 *              NOT healthy: an empty `queues` array here means "we did not
 *              ask", and rendering it as "no unhealthy queues" would rebuild
 *              at the client the exact collapse the endpoint prevents.
 *   healthy    every queue accounted for and fine. Says so, and names nothing.
 *   malformed  a 200 whose body carried no `status`. A real surprise; it is
 *              not a verdict and must not be shown as one.
 *   unreadable the read itself did not complete (refused, unreachable, 404).
 *              Not a healthy queue and not an unhealthy one.
 *
 * ===========================================================================
 * `inert` IS NOT A FAILURE, AND THAT IS LOAD-BEARING
 * ===========================================================================
 *
 * `system.evv-outbox-drain` throws `evv_adapter_missing` on every fire — 96
 * deliberate failures a day, because no transmission adapter exists yet and the
 * blocker is owner-held. `queueHealth.ts` disposes exactly that shape `inert`
 * and scores it `ok`. This module honours that score.
 *
 * It matters more than it looks. A console that listed inert queues among its
 * failures would be red from birth, permanently, for a condition nobody is
 * going to fix this week — and a surface that always says "problem" gets muted
 * just as fast as one that always says "fine", with the same result: the next
 * real failure arrives somewhere nobody reads. Declared silence stays silent.
 *
 * ===========================================================================
 * WHAT IS DELIBERATELY NOT READ OUT OF THIS BODY
 * ===========================================================================
 *
 * No organization ids, names or counts, and no job payloads — the endpoint does
 * not carry them, and this module does not reach for the one field that comes
 * close (`tenants.byStatus` is rendered by the customer directory's own
 * surface, not smuggled into a queue-health sentence). Queue names are
 * compile-time constants from `jobs/config.ts`; every other field read here is
 * an integer or a member of a closed enum.
 */
import type { ConsoleOutcome } from './client.js';

/**
 * The dispositions `jobs/queueHealth.ts` can return. Mirrored rather than
 * imported: the console shares no build with the API and must not learn to.
 */
export type QueueDisposition =
  | 'idle'
  | 'draining'
  | 'inert'
  | 'backlogged'
  | 'unknown'
  | 'runtime_gap'
  | 'paused'
  | 'blocked'
  | 'failing'
  | 'stuck'
  | 'silent'
  | 'stopped';

export type QueueSeverity = 'ok' | 'degraded' | 'error';

export interface JobsHealthQueueRow {
  queue?: unknown;
  disposition?: unknown;
  severity?: unknown;
  ready?: unknown;
  active?: unknown;
  unexpectedFailuresInWindow?: unknown;
  oldestReadyAgeSeconds?: unknown;
  oldestActiveAgeSeconds?: unknown;
}

export interface JobsHealthBody {
  status?: string;
  observedAt?: string;
  executor?: { scope?: unknown; state?: unknown; [key: string]: unknown };
  queues?: {
    severity?: unknown;
    source?: unknown;
    withheldReason?: unknown;
    scope?: unknown;
    queues?: JobsHealthQueueRow[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/** One queue an operator has to do something about. */
export interface QueueFailure {
  readonly queue: string;
  readonly disposition: QueueDisposition;
  readonly severity: QueueSeverity;
  /** Null, not zero, when the probe withheld the count. */
  readonly ready: number | null;
  readonly active: number | null;
  readonly unexpectedFailuresInWindow: number | null;
  readonly oldestAgeSeconds: number | null;
}

export type JobsHealthReading =
  | {
      readonly kind: 'unhealthy';
      readonly status: string;
      readonly failures: readonly QueueFailure[];
      /** Set when THIS INSTANCE's executor is the fault, which is not a queue fault. */
      readonly executorFault: string | null;
    }
  | { readonly kind: 'paused'; readonly status: string; readonly queues: readonly string[] }
  | { readonly kind: 'unknown'; readonly status: string; readonly queues: readonly string[] }
  | { readonly kind: 'withheld'; readonly status: string; readonly reason: string }
  | { readonly kind: 'healthy'; readonly status: string; readonly queuesObserved: number }
  | { readonly kind: 'malformed' }
  | { readonly kind: 'unreadable'; readonly message: string };

/**
 * Dispositions that mean an operator has work to do.
 *
 * `backlogged` is in: it is only `degraded`, but a growing backlog is
 * actionable and the roll-up word alone cannot separate it from a deploy
 * pause. `paused`, `unknown` and `runtime_gap` are out — each has its own
 * reading below, because calling any of them a failure is how a surface earns
 * its way into being ignored. `inert` is out for the reason in the docblock.
 */
const INCIDENT_DISPOSITIONS: ReadonlySet<string> = new Set<QueueDisposition>([
  'blocked',
  'failing',
  'stuck',
  'silent',
  'stopped',
  'backlogged',
]);

/** Dispositions that are an admission rather than a verdict. */
const UNKNOWING_DISPOSITIONS: ReadonlySet<string> = new Set<QueueDisposition>([
  'unknown',
  'runtime_gap',
]);

/**
 * Executor states that are a fault of THIS INSTANCE rather than of any queue.
 *
 * Mirrors the API's own severity map: the three it scores `error`, plus
 * `stopped`. `disabled` is excluded because the operator asked for it, and
 * `starting` / `not-started` because neither is a fault — one is transient and
 * the other means no executor is wired in this process at all, which the
 * withheld branch already reports honestly.
 */
const EXECUTOR_FAULTS: ReadonlySet<string> = new Set([
  'stalled',
  'failed',
  'fence-unasserted',
  'stopped',
]);

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readQueue(row: JobsHealthQueueRow): QueueFailure | null {
  if (typeof row.queue !== 'string' || typeof row.disposition !== 'string') return null;
  return {
    queue: row.queue,
    disposition: row.disposition as QueueDisposition,
    severity: (typeof row.severity === 'string' ? row.severity : 'degraded') as QueueSeverity,
    ready: asNumberOrNull(row.ready),
    active: asNumberOrNull(row.active),
    unexpectedFailuresInWindow: asNumberOrNull(row.unexpectedFailuresInWindow),
    oldestAgeSeconds:
      asNumberOrNull(row.oldestReadyAgeSeconds) ?? asNumberOrNull(row.oldestActiveAgeSeconds),
  };
}

/**
 * Read the platform queue-health body into something an operator can act on.
 *
 * Order matters, and it is: a read that did not answer, then a body that is
 * not a verdict, then a probe that did not run, then incidents, then the two
 * non-incident admissions, then healthy. An incident outranks a pause because
 * a fleet with one failing queue and one paused queue needs the failing one
 * said first.
 */
export function describeJobsHealth(outcome: ConsoleOutcome<JobsHealthBody>): JobsHealthReading {
  if (outcome.kind !== 'ok') return { kind: 'unreadable', message: outcome.message };

  const body = outcome.value;
  // The route always answers 200 and carries its verdict in the body, so a
  // missing `status` is a genuine surprise rather than a queue state. It was
  // already treated as one before this change; it stays that way.
  if (!body || typeof body.status !== 'string') return { kind: 'malformed' };
  const status = body.status;

  const executorState = typeof body.executor?.state === 'string' ? body.executor.state : null;
  const executorFault = executorState && EXECUTOR_FAULTS.has(executorState) ? executorState : null;

  // WITHHELD IS NOT EMPTY. No counts were taken, so no count may be reported —
  // including the count zero. An executor fault is still worth saying here,
  // since it is this instance's own state and needs no probe to know.
  if (body.queues?.source === 'withheld') {
    if (executorFault) {
      return { kind: 'unhealthy', status, failures: [], executorFault };
    }
    return {
      kind: 'withheld',
      status,
      reason:
        typeof body.queues.withheldReason === 'string'
          ? body.queues.withheldReason
          : 'unstated_reason',
    };
  }

  const rows = Array.isArray(body.queues?.queues) ? body.queues.queues : [];
  const parsed = rows.map(readQueue).filter((q): q is QueueFailure => q !== null);

  const failures = parsed.filter((q) => INCIDENT_DISPOSITIONS.has(q.disposition));
  if (failures.length > 0 || executorFault) {
    return { kind: 'unhealthy', status, failures, executorFault };
  }

  const unknowing = parsed.filter((q) => UNKNOWING_DISPOSITIONS.has(q.disposition));
  if (unknowing.length > 0) {
    return { kind: 'unknown', status, queues: unknowing.map((q) => q.queue) };
  }

  const paused = parsed.filter((q) => q.disposition === 'paused');
  if (paused.length > 0) {
    return { kind: 'paused', status, queues: paused.map((q) => q.queue) };
  }

  return { kind: 'healthy', status, queuesObserved: parsed.length };
}
